import { useChatMessages, useChatStatus } from '@ai-sdk-tools/store';
import { useEffect } from 'react';
import type { UIMessageWithMetadata } from '../../../lib/types';
export interface UseAutoResumeParams {
  /**
   * Whether auto-resume is enabled.
   * When true, the hook will automatically resume streams when the last message is from the user.
   */
  autoResume: boolean;
  /**
   * The initial messages for the chat.
   * Used to check if the last message is from the user (indicating an interrupted stream).
   */
  initialMessages: UIMessageWithMetadata[];
  /**
   * Function to resume the stream.
   * Should be called when auto-resume is enabled and the last message is from the user.
   */
  resumeStream: () => void;
}

/**
 * Hook that automatically resumes interrupted chat streams.
 * Based on the Vercel AI Chatbot implementation: https://github.com/vercel/ai-chatbot/blob/d0b6f37aeee532b942245ee9d406312f79e2fabe/hooks/use-auto-resume.ts
 *
 * When autoResume is enabled and the last message in initialMessages is from the user,
 * this hook will automatically call resumeStream() to continue the interrupted stream.
 *
 * @example
 * ```tsx
 * function ChatComponent() {
 *   const chat = useChat({ resume: true, ... });
 *   const { resumeStream } = chat; // or from useChatActions
 *
 *   useAutoResume({
 *     autoResume: true,
 *     initialMessages: initialMessages,
 *     resumeStream,
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAutoResume({
  autoResume,
  initialMessages,
  resumeStream
}: UseAutoResumeParams) {
  // Use messages from the store to check current state
  // This ensures we're checking against the actual current messages
  const storeMessages = useChatMessages<UIMessageWithMetadata>();
  const status = useChatStatus();
  useEffect(() => {
    if (!autoResume) {
      return;
    }

    // Use store messages if available, otherwise fall back to initialMessages
    const messagesToCheck = storeMessages.length > 0 ? storeMessages : initialMessages;
    const mostRecentMessage = messagesToCheck.at(-1);

    // If the last message is from the user, it means the assistant's response was interrupted
    // and we should resume the stream
    if (mostRecentMessage?.role === 'user' && status === 'ready') {
      resumeStream();
    }

    // We intentionally run this when messages change or when autoResume/resumeStream changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoResume, initialMessages.length, storeMessages.length, resumeStream, status]);
}